import {
  Anchor as MantineAnchor,
  Code as MantineCode,
  Highlight as MantineHighlight,
  List as MantineList,
  Text as MantineText,
  Title as MantineTitle,
} from "@mantine/core";
import classNames from "classnames";
import * as React from "react";
import type { UrlObject } from "url";
import type { ElementProps, SizeValue, StyleProps } from "./style-props";

import classes from "./typography.module.css";

/**
 * The typographic props every text-bearing primitive shares.
 *
 * Two generations of the Mantine API are live in the call sites at once: the Mantine 6 shorthands
 * (`color`, `weight`, `align`, `transform`) and the style props that replace them (`c`, `fw`, `ta`,
 * `tt`). Both stay accepted. Mantine 7 removed most of the shorthands, so `toStyleProps` below
 * translates them onto the style props they became (SC-2703); an explicit style prop still wins,
 * as it did in Mantine 6.
 */
interface TypographyProps extends StyleProps {
  size?: SizeValue;
  color?: string;
  weight?: React.CSSProperties["fontWeight"];
  align?: React.CSSProperties["textAlign"];
  transform?: "capitalize" | "uppercase" | "lowercase" | "none";
  underline?: boolean;
  strikethrough?: boolean;
  italic?: boolean;
  inline?: boolean;
  inherit?: boolean;
  lineClamp?: number;
  truncate?: true | "start" | "end";
}

type Shorthand = "weight" | "align" | "transform" | "underline" | "strikethrough" | "italic";

/** The Mantine 6 shorthands Mantine 7 no longer accepts, as the style props they set. */
const toStyleProps = <Props extends TypographyProps>({
  weight,
  align,
  transform,
  underline,
  strikethrough,
  italic,
  ...props
}: Props): Omit<Props, Shorthand> => {
  const decoration = [underline && "underline", strikethrough && "line-through"].filter(Boolean).join(" ");

  return {
    ...props,
    fw: props.fw ?? weight,
    ta: props.ta ?? align,
    tt: props.tt ?? transform,
    td: props.td ?? (decoration || undefined),
    fs: props.fs ?? (italic ? "italic" : undefined),
  };
};

/**
 * Mantine 6's Text set the theme font family, inherited its font size when no `size` was given and
 * never set a font weight; Mantine 7's sets no family, renders an unsized Text at `md` and sets
 * `font-weight: normal`. The wrapper keeps the Mantine 6 defaults for every Text-based primitive (Text,
 * Anchor, Highlight) as the single-class rules in `typography.module.css` (SC-2703), not as inline style
 * props, so a caller's `style`, `className`, style props, `size` and `weight` still win over them, as
 * they won over Mantine 6's class. An `inherit` Text takes neither class: Mantine 7's own `inherit` rule
 * inherits size, weight and line height, and with no family set the family inherits too, as in Mantine 6.
 */
const withTextDefaults = <Props extends TypographyProps & { className?: string }>(props: Props): Props => ({
  ...props,
  className: classNames(
    !props.inherit && classes.textDefaults,
    !props.inherit && props.size === undefined && classes.unsizedText,
    props.className,
  ),
});

export interface TextProps extends ElementProps<"div">, TypographyProps {
  /**
   * The root element. 60 of the 61 call sites pass a plain `"span"`, `"div"`, `"a"`, or `"summary"`,
   * and a closed union of those was the first design here. It is open because the 61st passes the
   * app's `OrgLink`, and the alternative — rendering that one as an `Anchor` instead — is not
   * equivalent: `Anchor` carries the static `mantine-Anchor-root` class, which `src/styles/globals.css`
   * targets with a hardcoded `color: #006cb8`. Narrowing the type would have silently recoloured a link.
   */
  component?: React.ElementType;
  /** Mantine 6 shorthand for `component="span"`. */
  span?: boolean;
  href?: string;
  target?: string;
  rel?: string;
}

/**
 * Mantine types `Text` as a polymorphic union whose `component` arm selects the element prop set.
 * A non-generic wrapper cannot restate that: without this alias the declared type collapses to
 * `component?: "div" | undefined` and the 55 `component="span"` call sites stop compiling.
 *
 * The alias flattens that union, and flattening loses a real constraint — `component` no longer
 * selects which element props are required, so a combination Mantine would reject (`component="summary"`
 * alongside `href`) type-checks here. No call site does that, and `typography-casts.test.ts` pins the
 * assumption this cast actually rests on: that Mantine accepts every prop these types declare.
 */
const TextRoot = MantineText as React.ComponentType<TextProps & { readonly ref?: React.Ref<HTMLDivElement> }>;

/**
 * Mantine 6's Text rendered a `div` (or a `span` for `span`); Mantine 7's defaults to `p`, which cannot
 * hold the block content call sites put in it. An explicit `component` still wins, as it did.
 */
export const Text = React.forwardRef<HTMLDivElement, TextProps>(function Text({ children, component, ...props }, ref) {
  return (
    <TextRoot {...toStyleProps(withTextDefaults(props))} component={component ?? (props.span ? "span" : "div")} ref={ref}>
      {children}
    </TextRoot>
  );
});

export interface TitleProps extends ElementProps<"h1">, TypographyProps {
  order?: 1 | 2 | 3 | 4 | 5 | 6;
}

const TRUNCATE_STYLE: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const TRUNCATE_START_STYLE: React.CSSProperties = { ...TRUNCATE_STYLE, direction: "rtl", textAlign: "right" };

/**
 * Mantine 6 rendered Title as a Text whose own heading class overrode Text's font family, size, weight
 * and line height — so Title's `inherit` never showed, and is not forwarded. Mantine 7's Title is not a
 * Text: `color`, `inline` and `truncate` become the style they produced.
 */
export const Title = React.forwardRef<HTMLHeadingElement, TitleProps>(function Title(
  { children, color, inline, inherit: _inherit, truncate, style, ...props },
  ref,
) {
  return (
    <MantineTitle
      {...toStyleProps(props)}
      c={props.c ?? color}
      lh={props.lh ?? (inline ? 1 : undefined)}
      style={truncate ? { ...(truncate === "start" ? TRUNCATE_START_STYLE : TRUNCATE_STYLE), ...style } : style}
      ref={ref}
    >
      {children}
    </MantineTitle>
  );
});

interface AnchorBaseProps extends Omit<ElementProps<"a">, "href">, TypographyProps {}

/**
 * `component` is split rather than a single `ElementType`, because the two kinds of value carry
 * different obligations and the root cast below cannot enforce either:
 *
 * - An **intrinsic element** — `"button"` and `"span"` in this repo — takes no `href`.
 * - A **component** — Next's `Link`, the app's `OrgLink` — is a routing target and `href` is what it
 *   routes to. Without this split `<Anchor component={Link}>` type-checks and reaches `Link` with no
 *   URL at all.
 *
 * The component arm stays open because `Link` and `OrgLink` cannot be named from inside `src/ui`
 * without inverting the dependency; SC-2703 owns closing it once the routing wrappers move behind
 * the boundary.
 */
export type AnchorProps = AnchorBaseProps &
  (
    | { component?: keyof React.JSX.IntrinsicElements; href?: string }
    | { component: Exclude<React.ElementType, keyof React.JSX.IntrinsicElements>; href: string | UrlObject }
  );

/** Mantine 7's Anchor `underline` modes; the owned boolean maps onto the two Mantine 6 meant. */
type AnchorUnderline = "hover" | "never";

/**
 * Same flattening as `TextRoot`: without the alias the declared type collapses to
 * `component?: "a" | undefined`. The one obligation the flattening would otherwise lose — a routing
 * `component` needs its `href` — is restated by the union on `AnchorProps` above rather than left to
 * the cast, and `typography-casts.test.ts` fails to compile if that union is removed. The root takes
 * Mantine 7's `underline` mode, which the casts test pins to Mantine's own type.
 */
const AnchorRoot = MantineAnchor as React.ComponentType<
  Omit<AnchorBaseProps, "underline"> & {
    component?: React.ElementType;
    href?: string | UrlObject;
    underline?: AnchorUnderline;
    readonly ref?: React.Ref<HTMLAnchorElement>;
  }
>;

/**
 * Mantine 6's Anchor underlined on hover unless `underline={false}`; the prop never reached the text
 * decoration. Mantine 7 spells those two cases `"hover"` (its default) and `"never"`.
 *
 * Mantine 6 emitted `c` as a class prepended to the document head, so the global link colour (`globals.css`
 * `[data-sc-anchor]`, equal specificity, later) won over it; Mantine 7 writes `c` inline, which wins over every
 * rule. A colour string goes to Mantine as the Text `color` instead, which Mantine resolves into the element's
 * `--text-color` for the single-class `anchorColor` rule to apply, so the global rule (and a caller's `style`)
 * wins again (SC-2703). The colour is unchanged: the forced light scheme defines the `-text` variable `c` names
 * as the `-filled` one `color` names. A responsive `c` object stays a style prop, since `color` takes one value
 * and a primitive has no theme to resolve each breakpoint from; no call site passes one.
 */
export const Anchor = React.forwardRef<HTMLAnchorElement, AnchorProps>(function Anchor(
  { children, underline, c, className, ...props },
  ref,
) {
  const color = typeof c === "string" && c !== "" ? c : undefined;

  return (
    <AnchorRoot
      {...toStyleProps(withTextDefaults({ ...props, className: classNames(color !== undefined && classes.anchorColor, className) }))}
      c={color === undefined ? c : undefined}
      color={color ?? props.color}
      underline={underline === false ? "never" : undefined}
      data-sc-anchor="true"
      ref={ref}
    >
      {children}
    </AnchorRoot>
  );
});

export interface ListProps extends ElementProps<"ul">, StyleProps {
  type?: "ordered" | "unordered";
  size?: SizeValue;
  spacing?: SizeValue;
  withPadding?: boolean;
  center?: boolean;
  icon?: React.ReactNode;
  listStyleType?: React.CSSProperties["listStyleType"];
}

export interface ListItemProps extends ElementProps<"li">, StyleProps {
  icon?: React.ReactNode;
}

/*
 * Mantine 6's List set theme.lineHeight on the list and its items at every `size` and indented `withPadding` by
 * theme.spacing.xl (6.0.19 List/List.styles.js:27,29, List/ListItem/ListItem.styles.js:12). Mantine 7 takes the
 * line height from `size` (7.17.8 List.mjs:25-31, 1.4 at `xs`) and indents by `md` (styles/List.css:12-14, 12px
 * against 20px). The line height is restated through Mantine's own `--list-lh`, which it writes inline once a
 * `size` is given, and the indent as a single class that outranks Mantine's `:where` rule (SC-2703).
 */
const mantine6ListVars = () => ({ root: { "--list-lh": "var(--mantine-line-height)" } });

/**
 * Mantine renders `<ul>` for the default `type="unordered"` and `<ol>` otherwise. Mantine 6 typed the
 * ref as `HTMLOListElement` in both cases and the owned ref kept that; Mantine 7 types it as
 * `HTMLUListElement`. Neither element type is assignable to the other under strict function types, so
 * this alias restates the owned ref at the seam instead of changing the public one — the same flattening
 * as `TextRoot`, and `typography-casts.test.ts` pins every owned List prop to Mantine's own.
 */
const ListVendorRoot = MantineList as React.ComponentType<
  ListProps & { readonly ref?: React.Ref<HTMLOListElement>; vars?: typeof mantine6ListVars }
>;

const ListRoot = React.forwardRef<HTMLOListElement, ListProps>(function ListRoot({ children, className, ...props }, ref) {
  return (
    <ListVendorRoot
      {...props}
      className={classNames(props.withPadding && classes.listPadding, className)}
      ref={ref}
      vars={mantine6ListVars}
    >
      {children}
    </ListVendorRoot>
  );
});

const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(function ListItem({ children, ...props }, ref) {
  return (
    <MantineList.Item {...props} ref={ref}>
      {children}
    </MantineList.Item>
  );
});

export const List = Object.assign(ListRoot, { Item: ListItem });

export interface CodeProps extends ElementProps<"code">, StyleProps {
  block?: boolean;
  color?: string;
}

export const Code = React.forwardRef<HTMLElement, CodeProps>(function Code({ children, ...props }, ref) {
  return (
    <MantineCode {...props} ref={ref}>
      {children}
    </MantineCode>
  );
});

export interface HighlightProps extends ElementProps<"div">, StyleProps {
  /** Substring or substrings to highlight in the child text. */
  highlight: string | string[];
  highlightColor?: string;
  children: string;
}

/**
 * `Highlight` is polymorphic in Mantine. Flattening its default div root keeps the wrapper's
 * owned prop surface while the cast test pins every vendor prop this adapter forwards. Mantine 7
 * renamed `highlightColor` to `color`; the owned name is unchanged.
 */
const HighlightRoot = MantineHighlight as React.ComponentType<
  Omit<HighlightProps, "highlightColor"> & { color?: string; component?: "div"; readonly ref?: React.Ref<HTMLDivElement> }
>;

/** Mantine 7's Highlight renders its Text as the Mantine 7 `p` default; Mantine 6's rendered a `div`. */
export const Highlight = React.forwardRef<HTMLDivElement, HighlightProps>(function Highlight({ children, highlightColor, ...props }, ref) {
  return (
    <HighlightRoot {...withTextDefaults(props)} color={highlightColor} component="div" ref={ref}>
      {children}
    </HighlightRoot>
  );
});
